import { useMemo } from 'react';
import { useToastQueue } from "../../internal/ToastQueueProvider";

/**
 * This is the hook that can be used when you want to show a short-lived,
 * floating message to the user.  If you want to show a stand-alone message in
 * the document flow, you probably want to see "Notice" under the "Components"
 * section
 *
 * @note Implementation Notes
 * - It intentionally does not support actions at the moment because those are
 *   hard to do in an accessible manner.  If a use-case comes up, we can
 *   investigate further how to do this accessibly.  The "Notice" component
 *   itself supports actions though
 *
 *
 * @returns
 */
export function useToasts() {
  var toastQueue = useToastQueue();
  return useMemo(function () {
    return {
      primary: function primary(text) {
        if (typeof text !== 'string') {
          throw new Error('The parameter passed to toasts.primary must be a string');
        }
        toastQueue.push({
          intent: 'primary',
          text: text
        });
      },
      secondary: function secondary(text) {
        if (typeof text !== 'string') {
          throw new Error('The parameter passed to toasts.secondary must be a string');
        }
        toastQueue.push({
          intent: 'secondary',
          text: text
        });
      },
      negative: function negative(error) {
        if (!(error instanceof Error)) {
          throw new Error('The parameter passed to toasts.negative must be an instance of Error');
        }
        toastQueue.push({
          intent: 'negative',
          text: error.message
        });
      },
      positive: function positive(text) {
        if (typeof text !== 'string') {
          throw new Error('The parameter passed to toasts.positive must be a string');
        }
        toastQueue.push({
          intent: 'positive',
          text: text
        });
      },
      warning: function warning(text) {
        if (typeof text !== 'string') {
          throw new Error('The parameter passed to toasts.warning must be a string');
        }
        toastQueue.push({
          intent: 'warning',
          text: text
        });
      }
    };
  }, [toastQueue]);
}